import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Container,
    Paper,
    TextField,
    Button,
    Typography,
    Alert,
    Grid,
    Card,
    CardContent,
    CardActions,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    FormControlLabel,
    Switch,
    Chip,
    CircularProgress,
    Snackbar,
    Tabs,
    Tab,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Download as DownloadIcon, People as PeopleIcon, InsertPhoto as ImageIcon } from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import axios from 'axios';
import { workshopDomain } from '../../util/domain';
import { getEventTypeLabel, getEventTypeColor } from '../../util/eventTypes';

const EventManagement = () => {
    const navigate = useNavigate();
    const [events, setEvents] = useState([]);
    const [open, setOpen] = useState(false);
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [editingEvent, setEditingEvent] = useState(null);
    const [notification, setNotification] = useState({ open: false, message: '', severity: 'success' });
    const [activeTab, setActiveTab] = useState(0); // 0 for active events, 1 for inactive events
    const [formData, setFormData] = useState({
        eventType: 'WORKSHOP',
        title: '',
        description: '',
        registrationPageContent: '',
        termsAndConditions: '',
        termsAcceptanceRequired: false,
        venue: '',
        registrationFee: '',
        capacity: '',
        registrationOpenDate: null,
        registrationCloseDate: null,
        eventStartDate: null,
        eventEndDate: null,
        isOnline: false,
        meetingLink: '',
        meetingPassword: '',
    });
    const [bannerFile, setBannerFile] = useState(null);
    const [currentBannerUrl, setCurrentBannerUrl] = useState(null);
    const [bannerVersion, setBannerVersion] = useState(0);
    const [imageRefreshTimestamp, setImageRefreshTimestamp] = useState(Date.now());
    const [downloadingEvent, setDownloadingEvent] = useState(null);
    const [downloadingStudents, setDownloadingStudents] = useState(null); // Track which event's student list is being downloaded
    const [registrationCounts, setRegistrationCounts] = useState({}); // Store counts by event hash_id
    const [fetchingCount, setFetchingCount] = useState(null); // Track which event count is being fetched

    // Check user role and authentication on mount
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) {
            navigate('/admin');
            return;
        }

        const user = JSON.parse(localStorage.getItem('user') || '{}');
        // Check if user has Admin role
        const roles = user.roles || [];
        const isAdmin = roles.includes('Admin');
        if (!isAdmin) {
            navigate('/admin/home');
            return;
        }

        fetchEvents();
    }, [navigate]);

    // Cleanup object URLs when banner file changes
    useEffect(() => {
        return () => {
            if (bannerFile) {
                const url = URL.createObjectURL(bannerFile);
                URL.revokeObjectURL(url);
            }
        };
    }, [bannerFile]);

    const fetchEvents = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await axios.get(`${workshopDomain}/api/events/all`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (response.data.success) {
                setEvents(response.data.events);
            }
        } catch (error) {
            setError('Failed to fetch events');
        }
    };

    const handleOpen = (event = null) => {
        if (event) {
            console.log('Opening event for editing:', event);
            setEditingEvent(event);

            // Parse dates with explicit time handling
            const parseDate = (dateString) => {
                if (!dateString) return null;
                const date = new Date(dateString);
                console.log(`Parsed ${dateString} to:`, date);
                return date;
            };

            setFormData({
                eventType: event.event_type,
                title: event.title,
                description: event.description,
                registrationPageContent: event.registration_page_content || '',
                termsAndConditions: event.terms_and_conditions || '',
                termsAcceptanceRequired: event.terms_acceptance_required === 1 || event.terms_acceptance_required === true,
                venue: event.venue,
                // Convert 0 to '0' to ensure Free Event switch is checked
                registrationFee: parseFloat(event.registration_fee) === 0 || event.registration_fee === '0' ? '0' : event.registration_fee,
                capacity: event.capacity,
                registrationOpenDate: parseDate(event.registration_open_date),
                registrationCloseDate: parseDate(event.registration_close_date),
                eventStartDate: parseDate(event.event_start_date),
                eventEndDate: parseDate(event.event_end_date),
                isOnline: event.is_online === 1 || event.is_online === true,
                meetingLink: event.meeting_link || '',
                meetingPassword: event.meeting_password || '',
            });
            setBannerFile(null);
            // Set the current banner URL for preview with cache-busting
            const bannerUrl = `${workshopDomain}/workshop/${event.hash_id}.jpeg?v=${Date.now()}`;
            setCurrentBannerUrl(bannerUrl);
            setBannerVersion(Date.now());
        } else {
            setEditingEvent(null);
            setFormData({
                eventType: 'WORKSHOP',
                title: '',
                description: '',
                registrationPageContent: '',
                termsAndConditions: '',
                termsAcceptanceRequired: false,
                venue: '',
                registrationFee: '',
                capacity: '',
                registrationOpenDate: null,
                registrationCloseDate: null,
                eventStartDate: null,
                eventEndDate: null,
                isOnline: false,
                meetingLink: '',
                meetingPassword: '',
            });
            setBannerFile(null);
            setCurrentBannerUrl(null);
        }
        setOpen(true);
    };

    const handleClose = () => {
        setOpen(false);
        setError('');
    };

    const handleChange = (e) => {
        const { name, value, checked } = e.target;
        setFormData({
            ...formData,
            [name]: name === 'isOnline' ? checked : value,
        });
    };

    const handleDateChange = (name) => (date) => {
        console.log(`Setting ${name} to:`, date);
        // Ensure we have a valid date object
        if (date && !isNaN(new Date(date).getTime())) {
            // Create a new Date object to ensure we're not using references
            const newDate = new Date(date);
            setFormData({
                ...formData,
                [name]: newDate,
            });
        } else {
            console.warn(`Invalid date for ${name}:`, date);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        // Validate required fields
        if (!formData.title || !formData.description ||
            (!formData.isOnline && !formData.venue) ||
            !formData.capacity ||
            !formData.registrationOpenDate || !formData.registrationCloseDate ||
            !formData.eventStartDate || !formData.eventEndDate) {
            setError('Please fill in all required fields');
            setLoading(false);
            return;
        }

        // Validate dates
        if (formData.registrationCloseDate <= formData.registrationOpenDate) {
            setError('Registration close date must be after registration open date');
            setLoading(false);
            return;
        }

        if (formData.eventEndDate <= formData.eventStartDate) {
            setError('Event end date must be after event start date');
            setLoading(false);
            return;
        }

        try {
            const token = localStorage.getItem('token');

            // Ensure all date fields are properly formatted for the API
            const formattedData = {
                ...formData,
                registrationFee: formData.registrationFee === '' ? 0 : Number(formData.registrationFee),
                capacity: Number(formData.capacity),
                // Format dates using the helper function
                registrationOpenDate: formData.registrationOpenDate,
                registrationCloseDate: formData.registrationCloseDate,
                eventStartDate: formData.eventStartDate,
                eventEndDate: formData.eventEndDate,
            };

            console.log('Submitting event data:', formattedData);

            if (editingEvent) {
                console.log('Updating event:', editingEvent.hash_id);

                // Use FormData if banner file is uploaded during edit
                if (bannerFile) {
                    const formDataObj = new FormData();
                    Object.keys(formattedData).forEach(key => {
                        formDataObj.append(key, formattedData[key]);
                    });
                    formDataObj.append('banner', bannerFile);
                    const response = await axios.put(`${workshopDomain}/api/events/${editingEvent.hash_id}`, formDataObj, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            // Let axios set Content-Type with boundary
                        }
                    });
                    console.log('Update response:', response.data);
                } else {
                    const response = await axios.put(`${workshopDomain}/api/events/${editingEvent.hash_id}`, formattedData, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    console.log('Update response:', response.data);
                }
            } else {
                // Use FormData if banner file is uploaded, otherwise use JSON
                if (bannerFile) {
                    const formDataObj = new FormData();
                    Object.keys(formattedData).forEach(key => {
                        formDataObj.append(key, formattedData[key]);
                    });
                    formDataObj.append('banner', bannerFile);
                    // Let axios set Content-Type automatically for multipart/form-data
                    await axios.post(`${workshopDomain}/api/events`, formDataObj, {
                        headers: {
                            Authorization: `Bearer ${token}`,
                            // Don't set Content-Type, let axios set it with boundary
                        }
                    });
                } else {
                    await axios.post(`${workshopDomain}/api/events`, formattedData, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                }
            }

            // Reload events to show updated content
            await fetchEvents();

            // Update image refresh timestamp to force banner reload
            setImageRefreshTimestamp(Date.now());

            // Close dialog after data is reloaded
            handleClose();

            // Show success notification
            setNotification({
                open: true,
                message: editingEvent ? 'Event updated successfully' : 'Event created successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error saving event:', error);
            setError(error.response?.data?.message || 'Failed to save event');
            setNotification({
                open: true,
                message: error.response?.data?.message || 'Failed to save event',
                severity: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    const getStatusColor = (status) => {
        switch (status) {
            case 'OPEN':
                return 'success';
            case 'UPCOMING':
                return 'warning';
            case 'CLOSED':
                return 'error';
            default:
                return 'default';
        }
    };

    const handleCloseNotification = () => {
        setNotification({ ...notification, open: false });
    };

    const handleTabChange = (event, newValue) => {
        setActiveTab(newValue);
    };

    // Filter and sort events based on active tab
    const filteredEvents = events
        .filter(event => {
            if (activeTab === 0) {
                // Active events: OPEN or UPCOMING
                return event.registration_status === 'OPEN' || event.registration_status === 'UPCOMING';
            } else {
                // Inactive events: CLOSED
                return event.registration_status === 'CLOSED';
            }
        })
        .sort((a, b) => {
            // Sort by event start date in descending order (latest first)
            const dateA = new Date(a.event_start_date);
            const dateB = new Date(b.event_start_date);
            return dateB - dateA;
        });

    const handleGetRegistrationCount = async (eventHashId) => {
        try {
            setFetchingCount(eventHashId);
            const token = localStorage.getItem('token');
            const response = await axios.get(
                `${workshopDomain}/api/events/${eventHashId}/registrations/count`,
                {
                    headers: { Authorization: `Bearer ${token}` }
                }
            );

            if (response.data.success) {
                setRegistrationCounts(prev => ({
                    ...prev,
                    [eventHashId]: response.data.count
                }));
                setNotification({
                    open: true,
                    message: `Total registrations: ${parseInt(response.data.count)}`,
                    severity: 'info'
                });
            }
        } catch (error) {
            console.error('Error fetching registration count:', error);
            setNotification({
                open: true,
                message: 'Failed to fetch registration count',
                severity: 'error'
            });
        } finally {
            setFetchingCount(null);
        }
    };

    const handleDownloadParticipants = async (eventHashId, eventTitle) => {
        try {
            setDownloadingEvent(eventHashId);
            const token = localStorage.getItem('token');
            // Use axios with responseType blob to handle file download
            const response = await axios.get(
                `${workshopDomain}/api/events/${eventHashId}/participants/download`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob' // Important for file downloads
                }
            );

            // Check if the response is empty or an error message
            if (response.data.size === 0) {
                setNotification({
                    open: true,
                    message: 'No participants found for this event',
                    severity: 'warning'
                });
                return;
            }

            // Create a blob URL and trigger download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${eventTitle.replace(/\s+/g, '_')}_participants.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            // Show success notification
            setNotification({
                open: true,
                message: 'Participants list downloaded successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error downloading participants:', error);
            // Show error notification
            setNotification({
                open: true,
                message: 'Failed to download participants list',
                severity: 'error'
            });
        } finally {
            setDownloadingEvent(null);
        }
    };

    const handleDownloadStudents = async (eventHashId, eventTitle) => {
        try {
            setDownloadingStudents(eventHashId);
            const token = localStorage.getItem('token');
            // Use axios with responseType blob to handle file download
            const response = await axios.get(
                `${workshopDomain}/api/events/${eventHashId}/students/download`,
                {
                    headers: { Authorization: `Bearer ${token}` },
                    responseType: 'blob' // Important for file downloads
                }
            );

            // Check if the response is empty or an error message
            if (response.data.size === 0) {
                setNotification({
                    open: true,
                    message: 'No students found for this event',
                    severity: 'warning'
                });
                return;
            }

            // Create a blob URL and trigger download
            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', `${eventTitle.replace(/\s+/g, '_')}_students.csv`);
            document.body.appendChild(link);
            link.click();
            link.remove();

            // Show success notification
            setNotification({
                open: true,
                message: 'Students list downloaded successfully',
                severity: 'success'
            });
        } catch (error) {
            console.error('Error downloading students:', error);
            // Show error notification
            setNotification({
                open: true,
                message: 'Failed to download students list',
                severity: 'error'
            });
        } finally {
            setDownloadingStudents(null);
        }
    };

    return (
        <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Container maxWidth="lg">
                <Box sx={{ mt: 4, mb: 4 }}>
                    <Grid container justifyContent="space-between" alignItems="center" sx={{ mb: 3 }}>
                        <Grid item>
                            <Typography variant="h4" component="h1">
                                Event Management
                            </Typography>
                        </Grid>
                        <Grid item>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => handleOpen()}
                            >
                                Create Event
                            </Button>
                        </Grid>
                    </Grid>

                    <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
                        <Tabs value={activeTab} onChange={handleTabChange} aria-label="event tabs">
                            <Tab label={`Active Events (${events.filter(e => e.registration_status === 'OPEN' || e.registration_status === 'UPCOMING').length})`} />
                            <Tab label={`Inactive Events (${events.filter(e => e.registration_status === 'CLOSED').length})`} />
                        </Tabs>
                    </Box>

                    <Grid container spacing={3}>
                        {filteredEvents.length === 0 ? (
                            <Grid item xs={12}>
                                <Paper sx={{ p: 3, textAlign: 'center' }}>
                                    <Typography variant="body1" color="text.secondary">
                                        No {activeTab === 0 ? 'active' : 'inactive'} events found
                                    </Typography>
                                </Paper>
                            </Grid>
                        ) : (
                            filteredEvents.map((event) => (
                                <Grid item xs={12} md={6} key={event.hash_id}>
                                    <Card>
                                        <CardContent>
                                            {/* Display banner if it exists */}
                                            <Box
                                                component="img"
                                                src={`${workshopDomain}/workshop/${event.hash_id}.jpeg?v=${imageRefreshTimestamp}`}
                                                key={`${event.hash_id}-${events.length}`}
                                                alt={event.title}
                                                sx={{
                                                    width: '100%',
                                                    objectFit: 'contain',
                                                    borderRadius: 1,
                                                    mb: 2,
                                                    display: 'block',
                                                    backgroundColor: '#f5f5f5'
                                                }}
                                                onError={(e) => {
                                                    e.target.style.display = 'none';
                                                }}
                                            />
                                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                                                <Box>
                                                    <Typography variant="h6" gutterBottom>
                                                        {event.title}
                                                    </Typography>
                                                    <Box sx={{ display: 'flex', gap: 1 }}>
                                                        <Chip
                                                            label={event.event_type}
                                                            color={getEventTypeColor(event.event_type)}
                                                            size="small"
                                                        />
                                                        <Chip
                                                            label={event.is_online ? 'Online' : 'Offline'}
                                                            variant="outlined"
                                                            size="small"
                                                        />
                                                    </Box>
                                                </Box>
                                                <Chip
                                                    label={event.registration_status}
                                                    color={getStatusColor(event.registration_status)}
                                                    size="small"
                                                />
                                            </Box>
                                            <Typography variant="body2" color="text.secondary">
                                                {event.description}
                                            </Typography>
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="body2">
                                                    <strong>Venue:</strong> {event.is_online ? 'Online Event' : event.venue}
                                                </Typography>
                                                {event.registration_fee > 0 && (
                                                    <Typography variant="body2">
                                                        <strong>Fee:</strong> ₹{event.registration_fee}
                                                    </Typography>
                                                )}
                                                <Typography variant="body2">
                                                    <strong>Capacity:</strong> {event.capacity}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>Registration:</strong> {new Date(event.registration_open_date).toLocaleString()} - {new Date(event.registration_close_date).toLocaleString()}
                                                </Typography>
                                                <Typography variant="body2">
                                                    <strong>{getEventTypeLabel(event.event_type)}:</strong> {new Date(event.event_start_date).toLocaleString()} - {new Date(event.event_end_date).toLocaleString()}
                                                </Typography>
                                                {event.is_online && (
                                                    <>
                                                        <Typography variant="body2">
                                                            <strong>Meeting Link:</strong> {event.meeting_link}
                                                        </Typography>
                                                        <Typography variant="body2">
                                                            <strong>Meeting Password:</strong> {event.meeting_password}
                                                        </Typography>
                                                    </>
                                                )}
                                                <Typography variant="body2" sx={{ mt: 1, fontFamily: 'monospace', color: 'text.disabled' }}>
                                                    <strong>Event Registration URL :</strong>
                                                    <a href={`${(process.env.REACT_APP_NODE_ENV === 'development') ? 'http://localhost:3000' : 'https://workshop.vectorindia.org'}/register/${event.hash_id}`} >{(process.env.REACT_APP_NODE_ENV === 'development') ? 'http://localhost:3000' : 'https://workshop.vectorindia.org'}/register/{event.hash_id}</a>
                                                </Typography>
                                            </Box>
                                        </CardContent>
                                        <CardActions>
                                            <Button
                                                size="small"
                                                startIcon={<EditIcon />}
                                                onClick={() => handleOpen(event)}
                                            >
                                                Edit
                                            </Button>
                                            <Button
                                                size="small"
                                                color="secondary"
                                                startIcon={fetchingCount === event.hash_id ? <CircularProgress size={16} /> : <PeopleIcon />}
                                                onClick={() => handleGetRegistrationCount(event.hash_id)}
                                                disabled={fetchingCount === event.hash_id}
                                            >
                                                {fetchingCount === event.hash_id ? 'Loading...' : (registrationCounts[event.hash_id] !== undefined ? `Count: ${parseInt(registrationCounts[event.hash_id])}` : 'Get Count')}
                                            </Button>
                                            <Button
                                                size="small"
                                                color="primary"
                                                startIcon={downloadingEvent === event.hash_id ? <CircularProgress size={16} /> : <DownloadIcon />}
                                                onClick={() => handleDownloadParticipants(event.hash_id, event.title)}
                                                disabled={downloadingEvent === event.hash_id}
                                            >
                                                {downloadingEvent === event.hash_id ? 'Downloading...' : 'Download For Webinar'}
                                            </Button>
                                            <Button
                                                size="small"
                                                color="secondary"
                                                startIcon={downloadingStudents === event.hash_id ? <CircularProgress size={16} /> : <DownloadIcon />}
                                                onClick={() => handleDownloadStudents(event.hash_id, event.title)}
                                                disabled={downloadingStudents === event.hash_id}
                                            >
                                                {downloadingStudents === event.hash_id ? 'Downloading...' : 'Download Students'}
                                            </Button>
                                        </CardActions>
                                    </Card>
                                </Grid>
                            )))}
                    </Grid>

                    <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                        <DialogTitle>
                            {editingEvent ? 'Edit Event' : 'Create Event'}
                        </DialogTitle>
                        <DialogContent>
                            {error && (
                                <Alert severity="error" sx={{ mb: 2 }}>
                                    {error}
                                </Alert>
                            )}

                            <Box component="form" noValidate sx={{ mt: 2 }}>
                                <Grid container spacing={2}>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth required>
                                            <InputLabel>Event Type</InputLabel>
                                            <Select
                                                name="eventType"
                                                value={formData.eventType}
                                                onChange={handleChange}
                                                label="Event Type"
                                            >
                                                <MenuItem value="WORKSHOP">Workshop</MenuItem>
                                                <MenuItem value="WEBINAR">Webinar</MenuItem>
                                                <MenuItem value="HACKATHON">Hackathon</MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={formData.isOnline}
                                                    onChange={handleChange}
                                                    name="isOnline"
                                                />
                                            }
                                            label="Online Event"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Button
                                            variant="outlined"
                                            component="label"
                                            fullWidth
                                            sx={{ py: 1.5 }}
                                        >
                                            {editingEvent ? 'Update Banner Image' : 'Upload Banner Image'}
                                            <input
                                                type="file"
                                                hidden
                                                accept="image/*"
                                                onChange={(e) => {
                                                    if (e.target.files && e.target.files[0]) {
                                                        setBannerFile(e.target.files[0]);
                                                    }
                                                }}
                                            />
                                        </Button>
                                        {bannerFile && (
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                                Selected: {bannerFile.name}
                                            </Typography>
                                        )}
                                        {editingEvent && !bannerFile && (
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                                                Leave empty to keep existing banner
                                            </Typography>
                                        )}
                                        {/* Show current banner preview when editing */}
                                        {editingEvent && currentBannerUrl && !bannerFile && (
                                            <Box sx={{ mt: 2, position: 'relative' }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                                    Current Banner:
                                                </Typography>
                                                <Box
                                                    component="img"
                                                    src={currentBannerUrl}
                                                    alt="Current banner"
                                                    sx={{
                                                        width: '100%',
                                                        maxHeight: '200px',
                                                        objectFit: 'contain',
                                                        borderRadius: 1,
                                                        border: '1px solid #e0e0e0',
                                                        backgroundColor: '#f5f5f5'
                                                    }}
                                                    onError={(e) => {
                                                        e.target.style.display = 'none';
                                                    }}
                                                />
                                            </Box>
                                        )}
                                        {/* Show new file preview */}
                                        {bannerFile && (
                                            <Box sx={{ mt: 2 }}>
                                                <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
                                                    New Banner Preview:
                                                </Typography>
                                                <Box
                                                    component="img"
                                                    src={URL.createObjectURL(bannerFile)}
                                                    alt="New banner preview"
                                                    sx={{
                                                        width: '100%',
                                                        maxHeight: '200px',
                                                        objectFit: 'contain',
                                                        borderRadius: 1,
                                                        border: '1px solid #e0e0e0',
                                                        backgroundColor: '#f5f5f5'
                                                    }}
                                                />
                                            </Box>
                                        )}
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            required
                                            fullWidth
                                            name="title"
                                            label="Event Title"
                                            value={formData.title}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            required
                                            fullWidth
                                            multiline
                                            rows={4}
                                            name="description"
                                            label="Description"
                                            value={formData.description}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={10}
                                            name="registrationPageContent"
                                            label="Registration Page Content (HTML)"
                                            value={formData.registrationPageContent}
                                            onChange={handleChange}
                                            helperText="HTML content displayed on the registration page above the form"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={6}
                                            name="termsAndConditions"
                                            label="Terms and Conditions (HTML)"
                                            value={formData.termsAndConditions}
                                            onChange={handleChange}
                                            helperText="HTML content for terms and conditions shown before the Register button"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={formData.termsAcceptanceRequired}
                                                    onChange={(e) => {
                                                        setFormData({
                                                            ...formData,
                                                            termsAcceptanceRequired: e.target.checked
                                                        });
                                                    }}
                                                    name="termsAcceptanceRequired"
                                                />
                                            }
                                            label="Require Terms Acceptance"
                                        />
                                    </Grid>
                                    {!formData.isOnline && (
                                        <Grid item xs={12}>
                                            <TextField
                                                required
                                                fullWidth
                                                name="venue"
                                                label="Venue"
                                                value={formData.venue}
                                                onChange={handleChange}
                                            />
                                        </Grid>
                                    )}
                                    {formData.isOnline && (
                                        <>
                                            <Grid item xs={12}>
                                                <TextField
                                                    required
                                                    fullWidth
                                                    name="meetingLink"
                                                    label="Meeting Link"
                                                    value={formData.meetingLink}
                                                    onChange={handleChange}
                                                />
                                            </Grid>
                                            <Grid item xs={12}>
                                                <TextField
                                                    fullWidth
                                                    name="meetingPassword"
                                                    label="Meeting Password"
                                                    value={formData.meetingPassword}
                                                    onChange={handleChange}
                                                />
                                            </Grid>
                                        </>
                                    )}
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <FormControlLabel
                                                control={
                                                    <Switch
                                                        checked={formData.registrationFee === '0' || formData.registrationFee === 0}
                                                        onChange={(e) => {
                                                            setFormData({
                                                                ...formData,
                                                                registrationFee: e.target.checked ? '0' : ''
                                                            });
                                                        }}
                                                        name="isFreeEvent"
                                                    />
                                                }
                                                label="Free Event"
                                            />
                                        </FormControl>
                                    </Grid>
                                    {(formData.registrationFee !== '0' && formData.registrationFee !== 0) && (
                                        <Grid item xs={12} sm={6}>
                                            <TextField
                                                required
                                                fullWidth
                                                type="number"
                                                name="registrationFee"
                                                label="Registration Fee"
                                                value={formData.registrationFee}
                                                onChange={handleChange}
                                                InputProps={{
                                                    startAdornment: '₹',
                                                }}
                                            />
                                        </Grid>
                                    )}
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            required
                                            fullWidth
                                            type="number"
                                            name="capacity"
                                            label="Capacity"
                                            value={formData.capacity}
                                            onChange={handleChange}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <DateTimePicker
                                            label="Registration Open Date"
                                            value={formData.registrationOpenDate}
                                            onChange={handleDateChange('registrationOpenDate')}
                                            renderInput={(params) => <TextField {...params} fullWidth required />}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    required: true,
                                                    error: !formData.registrationOpenDate
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <DateTimePicker
                                            label="Registration Close Date"
                                            value={formData.registrationCloseDate}
                                            onChange={handleDateChange('registrationCloseDate')}
                                            renderInput={(params) => <TextField {...params} fullWidth required />}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    required: true,
                                                    error: !formData.registrationCloseDate
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <DateTimePicker
                                            label={`${getEventTypeLabel(formData.eventType)} Start Date`}
                                            value={formData.eventStartDate}
                                            onChange={handleDateChange('eventStartDate')}
                                            renderInput={(params) => <TextField {...params} fullWidth required />}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    required: true,
                                                    error: !formData.eventStartDate
                                                }
                                            }}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <DateTimePicker
                                            label={`${getEventTypeLabel(formData.eventType)} End Date`}
                                            value={formData.eventEndDate}
                                            onChange={handleDateChange('eventEndDate')}
                                            renderInput={(params) => <TextField {...params} fullWidth required />}
                                            slotProps={{
                                                textField: {
                                                    fullWidth: true,
                                                    required: true,
                                                    error: !formData.eventEndDate
                                                }
                                            }}
                                        />
                                    </Grid>
                                </Grid>
                            </Box>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={handleClose}>Cancel</Button>
                            <Button
                                onClick={handleSubmit}
                                variant="contained"
                                disabled={loading}
                            >
                                {loading ? 'Saving...' : 'Save'}
                            </Button>
                        </DialogActions>
                    </Dialog>
                </Box>
                <Snackbar
                    open={notification.open}
                    autoHideDuration={6000}
                    onClose={handleCloseNotification}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={handleCloseNotification}
                        severity={notification.severity}
                        sx={{ width: '100%' }}
                    >
                        {notification.message}
                    </Alert>
                </Snackbar>
            </Container>
        </LocalizationProvider>
    );
};

export default EventManagement; 